import { useTranslation } from 'react-i18next';
import { balanceText, date, money } from './format';

/** One totals block: debit, credit and the balance with its side. */
function Block({ title, value, suffix, t }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3">
      <div className="mb-1 text-[12px] font-bold uppercase tracking-wide text-zinc-500">
        {title}
        {suffix ? ` (${suffix})` : ''}
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 text-[13px]">
        <dt className="text-zinc-500">{t('blocks.debit')}</dt>
        <dd className="m-0 text-right tabular-nums">{money(value.debit)}</dd>
        <dt className="text-zinc-500">{t('blocks.credit')}</dt>
        <dd className="m-0 text-right tabular-nums">{money(value.credit)}</dd>
        <dt className="border-t border-zinc-100 pt-0.5 font-semibold">{t('blocks.balance')}</dt>
        <dd className="m-0 border-t border-zinc-100 pt-0.5 text-right font-semibold tabular-nums">
          {balanceText(value, t)}
        </dd>
      </dl>
    </div>
  );
}

/**
 * The head of a card as AB Soft prints it: account, partner (with address
 * and tax ID when AB sent them), project and period; then the three totals
 * blocks — opening balance, the period, the total — each with debit, credit
 * and the balance with its side. Foreign-currency cards get the same three
 * blocks per currency.
 *
 * @param {Object} props
 * @param {Object} props.card - the card from the server
 */
export default function CardHead({ card }) {
  const { t } = useTranslation('ledgerCards');
  const { header } = card;
  const { partner } = header;
  const place = partner ? [partner.postalCode, partner.city].filter(Boolean).join(' ') : '';
  const address = partner ? [partner.address, place, partner.country].filter(Boolean).join(', ') : '';

  const rows = [
    [t('head.account'), header.account ? [header.account.code, header.account.name].filter(Boolean).join(' ') : t('head.allAccounts')],
    [t('head.partner'), partner ? [partner.code, partner.name].filter(Boolean).join(' ') : t('head.allPartners')],
    address && [t('head.address'), address],
    partner?.pib && [t('head.pib'), partner.pib],
    partner?.bankAccount && [t('head.bankAccount'), partner.bankAccount],
    header.project && [t('head.project'), [header.project.code, header.project.name].filter(Boolean).join(' ')],
    [t('head.period'), `${date(card.from)} – ${date(card.to)}`],
  ].filter(Boolean);

  const blocks = [
    ['opening', card.opening],
    ['period', card.period],
    ['total', card.total],
  ];

  return (
    <div className="grid gap-4">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[13px]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="font-semibold text-zinc-500">{label}</dt>
            <dd className="m-0 min-w-0 break-words">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {blocks.map(([key, value]) => (
          <Block key={key} title={t(`blocks.${key}`)} value={value} t={t} />
        ))}
      </div>
      {(card.fx || []).map((fx) => (
        <div key={fx.currency} className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Block title={t('blocks.opening')} suffix={fx.currency} value={fx.opening} t={t} />
          <Block title={t('blocks.period')} suffix={fx.currency} value={fx.period} t={t} />
          <Block title={t('blocks.total')} suffix={fx.currency} value={fx.total} t={t} />
        </div>
      ))}
    </div>
  );
}
